import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Vote, 
  Play, 
  Square, 
  CheckCircle2, 
  XCircle, 
  MinusCircle,
  Users,
  BarChart3,
  Loader2,
  RefreshCw
} from 'lucide-react';

const VoteLiveAG = ({ ag, user, isPresident, onVoteComplete }) => {
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showVoteModal, setShowVoteModal] = useState(false);
  const [voteHistory, setVoteHistory] = useState([]);
  const [newResolution, setNewResolution] = useState({ numero: 1, titre: '' });
  const [isClosing, setIsClosing] = useState(false);

  // Vérifier s'il y a une session active
  const checkActiveSession = useCallback(async () => {
    if (!ag?.id) return;
    
    try {
      const response = await apiClient.get(`/assemblees/${ag.id}/vote-sessions/active`);
      setActiveSession(response.data.active ? response.data.session : null);
    } catch (error) {
      console.error('Erreur vérification session:', error);
    }
  }, [ag?.id]);

  // Récupérer l'historique des votes
  const fetchVoteHistory = useCallback(async () => {
    if (!ag?.id) return;
    
    try {
      const response = await apiClient.get(`/assemblees/${ag.id}/vote-sessions/history`);
      setVoteHistory(response.data.sessions || []);
      
      // Déterminer le prochain numéro de résolution
      const closedSessions = response.data.sessions?.filter(s => s.status === 'closed') || [];
      setNewResolution(prev => ({
        ...prev,
        numero: closedSessions.length + 1
      }));
    } catch (error) {
      console.error('Erreur historique votes:', error);
    }
  }, [ag?.id]);

  // Polling pour les mises à jour en temps réel
  useEffect(() => {
    if (!ag?.id) return;

    checkActiveSession();
    fetchVoteHistory();

    // Polling toutes les 3 secondes si une session est active
    const interval = setInterval(() => {
      if (activeSession) {
        setPolling(true);
        checkActiveSession().finally(() => setPolling(false));
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [ag?.id, activeSession, checkActiveSession, fetchVoteHistory]);

  // Créer une nouvelle session de vote
  const handleCreateSession = async () => {
    if (!newResolution.titre.trim()) {
      toast.error('Veuillez entrer le titre de la résolution');
      return;
    }

    setLoading(true);
    try {
      await apiClient.post(`/assemblees/${ag.id}/vote-sessions`, {
        resolution_numero: newResolution.numero,
        resolution_titre: newResolution.titre
      });
      
      toast.success(`Vote ouvert pour la résolution n°${newResolution.numero}`);
      setShowCreateModal(false);
      setNewResolution(prev => ({ numero: prev.numero + 1, titre: '' }));
      await checkActiveSession();
      await fetchVoteHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du vote');
    } finally {
      setLoading(false);
    }
  };

  // Voter
  const handleVote = async (voteValue) => {
    if (!activeSession) return;

    setLoading(true);
    try {
      await apiClient.post(`/assemblees/${ag.id}/vote-sessions/${activeSession.id}/vote`, {
        vote: voteValue
      });
      
      toast.success(`Vote "${voteValue}" enregistré`);
      setShowVoteModal(false);
      await checkActiveSession();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du vote');
    } finally {
      setLoading(false);
    }
  };

  // Fermer la session de vote
  const handleCloseSession = async () => {
    if (!activeSession) return;

    setIsClosing(true);
    try {
      const response = await apiClient.post(`/assemblees/${ag.id}/vote-sessions/${activeSession.id}/close`);
      
      const resultat = response.data.resultat === 'adoptee' ? 'ADOPTÉE' : 'REJETÉE';
      toast.success(`Résolution ${resultat} - Pour: ${response.data.tantiemes_pour}, Contre: ${response.data.tantiemes_contre}`);
      
      setActiveSession(null);
      await fetchVoteHistory();
      
      if (onVoteComplete) {
        onVoteComplete(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la clôture du vote');
    } finally {
      setIsClosing(false);
    }
  };

  // Calculer les pourcentages
  const calculatePercentages = () => {
    if (!activeSession) return { pour: 0, contre: 0, abstention: 0 };
    
    const total = activeSession.tantiemes_pour + activeSession.tantiemes_contre + activeSession.tantiemes_abstention;
    if (total === 0) return { pour: 0, contre: 0, abstention: 0 };
    
    return {
      pour: Math.round((activeSession.tantiemes_pour / 1000) * 100),
      contre: Math.round((activeSession.tantiemes_contre / 1000) * 100),
      abstention: Math.round((activeSession.tantiemes_abstention / 1000) * 100)
    };
  };

  const percentages = calculatePercentages();

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4 md:p-6" data-testid="vote-live-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Vote className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-slate-900">Vote en direct</h3>
          {polling && <RefreshCw className="w-4 h-4 text-indigo-400 animate-spin" />}
        </div>
        
        {isPresident && !activeSession && (
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            size="sm"
            data-testid="start-vote-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Lancer un vote
          </Button>
        )}
      </div>

      {/* Session de vote active */}
      {activeSession ? (
        <div className="space-y-4" data-testid="active-vote-session">
          {/* En-tête de la résolution */}
          <div className="bg-white rounded-lg p-4 border border-indigo-100">
            <div className="flex items-start justify-between">
              <div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mb-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5 animate-pulse"></span>
                  Vote en cours
                </span>
                <h4 className="font-semibold text-slate-900">
                  Résolution n°{activeSession.resolution_numero}
                </h4>
                <p className="text-slate-600 mt-1">{activeSession.resolution_titre}</p>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Users className="w-4 h-4" />
                <span>{activeSession.total_votants} votant(s)</span>
              </div>
            </div>
          </div>

          {/* Barres de résultats en temps réel */}
          <div className="space-y-3">
            {/* Pour */}
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="flex items-center gap-1.5 text-green-700 font-medium">
                  <CheckCircle2 className="w-4 h-4" />
                  Pour
                </span>
                <span className="text-slate-600">{activeSession.tantiemes_pour} tantièmes ({percentages.pour}%)</span>
              </div>
              <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all duration-500"
                  style={{ width: `${percentages.pour}%` }}
                />
              </div>
            </div>

            {/* Contre */}
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="flex items-center gap-1.5 text-red-700 font-medium">
                  <XCircle className="w-4 h-4" />
                  Contre
                </span>
                <span className="text-slate-600">{activeSession.tantiemes_contre} tantièmes ({percentages.contre}%)</span>
              </div>
              <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-red-400 to-red-600 rounded-full transition-all duration-500"
                  style={{ width: `${percentages.contre}%` }}
                />
              </div>
            </div>

            {/* Abstention */}
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="flex items-center gap-1.5 text-slate-600 font-medium">
                  <MinusCircle className="w-4 h-4" />
                  Abstention
                </span>
                <span className="text-slate-600">{activeSession.tantiemes_abstention} tantièmes ({percentages.abstention}%)</span>
              </div>
              <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-slate-300 to-slate-500 rounded-full transition-all duration-500"
                  style={{ width: `${percentages.abstention}%` }}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-2">
            {/* Bouton voter (pour tous sauf si déjà voté) */}
            {!activeSession.user_vote ? (
              <Button
                onClick={() => setShowVoteModal(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white flex-1"
                data-testid="open-vote-modal-btn"
              >
                <Vote className="w-4 h-4 mr-2" />
                Voter maintenant
              </Button>
            ) : (
              <div className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-indigo-100 text-indigo-700 rounded-lg">
                <CheckCircle2 className="w-4 h-4" />
                <span>Vous avez voté : <strong>{activeSession.user_vote}</strong></span>
              </div>
            )}

            {/* Bouton clôturer (président uniquement) */}
            {isPresident && (
              <Button
                onClick={handleCloseSession}
                variant="outline"
                className="border-red-200 text-red-700 hover:bg-red-50"
                disabled={isClosing}
                data-testid="close-vote-btn"
              >
                {isClosing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Square className="w-4 h-4 mr-2" />
                )}
                Clôturer le vote
              </Button>
            )}
          </div>
        </div>
      ) : (
        /* Pas de session active */
        <div className="text-center py-6 text-slate-500">
          <BarChart3 className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p>Aucun vote en cours</p>
          {isPresident && (
            <p className="text-sm mt-1">Cliquez sur "Lancer un vote" pour commencer</p>
          )}
        </div>
      )}

      {/* Historique des votes */}
      {voteHistory.length > 0 && (
        <div className="mt-6 pt-4 border-t border-indigo-100">
          <h4 className="text-sm font-medium text-slate-700 mb-3">Historique des votes</h4>
          <div className="space-y-2">
            {voteHistory.filter(s => s.status === 'closed').map((session) => (
              <div 
                key={session.id}
                className="flex items-center justify-between bg-white rounded-lg p-3 border border-slate-100"
                data-testid={`vote-history-${session.resolution_numero}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    session.resultat === 'adoptee' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {session.resultat === 'adoptee' ? 'Adoptée' : 'Rejetée'}
                  </span>
                  <span className="text-sm text-slate-700">
                    <strong>N°{session.resolution_numero}</strong> - {session.resolution_titre?.substring(0, 40)}
                    {session.resolution_titre?.length > 40 && '...'}
                  </span>
                </div>
                <div className="text-xs text-slate-500">
                  {session.tantiemes_pour} pour / {session.tantiemes_contre} contre
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal création de vote */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Lancer un vote</DialogTitle>
            <DialogDescription>
              Ouvrez une session de vote pour une résolution
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Numéro de résolution</Label>
              <Input
                type="number"
                value={newResolution.numero}
                onChange={(e) => setNewResolution(prev => ({ ...prev, numero: parseInt(e.target.value) || 1 }))}
                min={1}
                data-testid="resolution-numero-input"
              />
            </div>
            <div>
              <Label>Titre de la résolution</Label>
              <Input
                value={newResolution.titre}
                onChange={(e) => setNewResolution(prev => ({ ...prev, titre: e.target.value }))}
                placeholder="Ex: Approbation des comptes 2024"
                data-testid="resolution-titre-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleCreateSession}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                disabled={loading}
                data-testid="confirm-start-vote-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Lancer le vote'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de vote */}
      <Dialog open={showVoteModal} onOpenChange={setShowVoteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Votez maintenant</DialogTitle>
            <DialogDescription>
              Résolution n°{activeSession?.resolution_numero}: {activeSession?.resolution_titre}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 mt-4">
            <Button
              onClick={() => handleVote('pour')}
              className="h-16 bg-green-600 hover:bg-green-700 text-white text-lg"
              disabled={loading}
              data-testid="vote-pour-btn"
            >
              <CheckCircle2 className="w-6 h-6 mr-3" />
              POUR
            </Button>
            <Button
              onClick={() => handleVote('contre')}
              className="h-16 bg-red-600 hover:bg-red-700 text-white text-lg"
              disabled={loading}
              data-testid="vote-contre-btn"
            >
              <XCircle className="w-6 h-6 mr-3" />
              CONTRE
            </Button>
            <Button
              onClick={() => handleVote('abstention')}
              variant="outline"
              className="h-16 border-2 border-slate-300 text-slate-600 hover:bg-slate-100 text-lg"
              disabled={loading}
              data-testid="vote-abstention-btn"
            >
              <MinusCircle className="w-6 h-6 mr-3" />
              ABSTENTION
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VoteLiveAG;
